import UserService from '@/services/UserService.ts'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import getModuleStatusList from '@/composables/getModuleStatusList.ts'
import {
  ExclusionRules,
  useWhitelabelExclusionRules,
} from '@/composables/useWhitelabelExclusionRules.ts'
import { FeatureList, useLDFlag } from '@/lib/ldClient.ts'
import {
  type AdviceModuleStatus,
  type AdviceModuleStatusMap,
  type AdviceModuleType,
  type InsuranceAdviceModuleStatus,
  PREMIUM_ADVICE_MODULES,
} from '@/types/adviceModules'

const { hasExclusionRule } = useWhitelabelExclusionRules()

// Re-export unified types for backward compatibility
export type ModuleStatus = AdviceModuleStatus
export type InsuranceModuleStatus = InsuranceAdviceModuleStatus
export type ModuleStatusesType = AdviceModuleStatusMap
export type ModuleType = AdviceModuleType

export const useModuleStatusesStore = defineStore('ModuleStatuses', () => {
  const possibleModuleStates: ModuleStatus[] = [
    'infoNeeded',
    'upgrade',
    'ready',
    'actioned',
    'processing',
  ]

  const premiumModules = [...PREMIUM_ADVICE_MODULES]

  const statuses = ref<ModuleStatusesType>({
    superInvestments: 'infoNeeded',
    salarySacrificeContributions: 'infoNeeded',
    lumpSumConcessionalContributions: 'infoNeeded',
    lumpSumNonConcessionalContributions: 'infoNeeded',
    personalInsurance: {
      single: { lifeCover: 'infoNeeded', incomeCover: 'infoNeeded', tpdCover: 'infoNeeded' },
      partner: { lifeCover: 'infoNeeded', incomeCover: 'infoNeeded', tpdCover: 'infoNeeded' },
    },
    debtPlan: 'infoNeeded',
    retirementPlan: 'infoNeeded',
  })
  type PersonalInsuranceUserType = keyof typeof statuses.value.personalInsurance

  const fetchModuleStatuses = async () => {
    try {
      const response = await UserService.getModuleStatuses()
      statuses.value = response.data
    } catch (e) {
      console.error('Error fetching module statuses', e)
    }
  }

  const isValidInsuranceCoverType = (type: string): boolean => {
    return ['life', 'tpd', 'income'].includes(type)
  }

  const isAdviceActioned = (
    type: string,
    userType: PersonalInsuranceUserType = 'single'
  ): boolean => {
    if (!isValidInsuranceCoverType(type)) return false
    const fullKey =
      `${type}Cover` as keyof (typeof statuses.value.personalInsurance)[PersonalInsuranceUserType]
    return statuses.value.personalInsurance[userType][fullKey] === 'actioned'
  }

  const getHighestPriorityState = (modulePaths: string | string[]): ModuleStatus => {
    // Follow a dot-path (e.g. "personalInsurance.single") from statuses.value
    const resolvePath = (path: string) =>
      path.split('.').reduce<any>((currentNode, segment) => currentNode?.[segment], statuses.value)

    // Normalize whatever we find into an array of ModuleStatus strings:
    // - If it's already a string (e.g. "ready"), wrap it in an array: ["ready"].
    // - If it's an object, recursively collect all leaf strings inside it.
    const collectStatuses = (node: unknown): ModuleStatus[] => {
      if (typeof node === 'string') return [node as ModuleStatus] // ← string leaf handled here
      if (node && typeof node === 'object')
        return Object.values(node as Record<string, unknown>).flatMap(collectStatuses)
      return []
    }

    const collectedStates = (Array.isArray(modulePaths) ? modulePaths : [modulePaths]).flatMap(
      (path) => collectStatuses(resolvePath(path))
    )

    // Choose the first status that appears in possibleModuleStates (highest priority first)
    const set = new Set(collectedStates)
    return possibleModuleStates.find((candidate) => set.has(candidate)) ?? 'infoNeeded'
  }

  const enableRetirement =
    !hasExclusionRule(ExclusionRules.RETIREMENT_PLAN) &&
    useLDFlag(FeatureList.ENABLE_RETIREMENT_PLAN, false).value

  const hasActionedAnyAdvice = () => {
    const { personalInsurance, ...otherModules } = statuses.value
    return (
      Object.values(otherModules).includes('actioned') ||
      Object.values(personalInsurance.single).includes('actioned') ||
      (statuses.value.retirementPlan === 'ready' && enableRetirement)
      // Retirement plan doesn't have an actioned (lock) state
    )
  }

  return {
    getStatuses: computed(() => statuses.value),
    options: getModuleStatusList,
    fetchModuleStatuses,
    isAdviceActioned,
    getHighestPriorityState,
    hasActionedAnyAdvice,
    premiumModules,
  }
})
